import { useMemo } from 'react'
import useSWRInfinite from 'swr/infinite'
import { getCatalogue } from 'src/lib/core/api-methods'
import { catalogueAdapter } from 'src/lib/utils/adapters'
import { ROWS_PER_PAGE } from './productSelectorConstants'
import { CatalogueDataType } from 'src/lib/types/common/catalogue'

interface UseProductSearchParams {
  accountId: string | undefined
  issuerId: string
  catalogueType: string
  searchQuery: string
  searchParameter: string
  sessionId: string
  includeBundleProducts?: boolean
}

export const useProductSearch = ({
  accountId,
  issuerId,
  catalogueType,
  searchQuery,
  searchParameter,
  sessionId,
  includeBundleProducts = true
}: UseProductSearchParams) => {
  const getKey = (pageIndex: number, previousPageData: any) => {
    if (previousPageData && !previousPageData.data?.length) return null
    if (!accountId || !issuerId) return null

    const key = `catalogue-${accountId}-${issuerId}-${catalogueType}-${searchQuery}-${searchParameter}-${includeBundleProducts}-${pageIndex}-${sessionId}`
    return key
  }

  // API Fetcher
  const fetcher = async (key: string) => {
    const parts = key.split('-')
    const pageIndexStr = parts[parts.length - 2]
    const pageIndex = parseInt(pageIndexStr || '0')

    const offset = pageIndex * ROWS_PER_PAGE + 1

    const apiCatalogueType = catalogueType === 'COLLECTION' ? 'collection' : 'product'

    const response: any = await getCatalogue(
      issuerId,
      accountId,
      offset,
      searchQuery,
      apiCatalogueType,
      searchParameter,
      ROWS_PER_PAGE,
      includeBundleProducts
    )

    if (response?.error) {
      throw new Error(response.error)
    }

    const adapterType = catalogueType === 'PRODUCT_AND_VARIANT' ? 'PRODUCT' : catalogueType
    const products = response?.data?.data?.map((row: any) => catalogueAdapter(row, adapterType)) || []

    return {
      data: products,
      totalCount: response?.data?.total_count || 0
    }
  }

  const { data, error, size, setSize, isValidating } = useSWRInfinite(getKey, fetcher, {
    revalidateFirstPage: false,
    revalidateOnFocus: false,
    revalidateOnMount: true,
    dedupingInterval: 2000
  })

  // Flatten the pages into a single list of products, deduplicating by ID
  const products = useMemo(() => {
    if (!data) return []
    const allProducts = data.flatMap(page => page.data)

    const uniqueProducts: Record<string, CatalogueDataType> = {}
    const result: CatalogueDataType[] = []

    allProducts.forEach((product: CatalogueDataType) => {
      if (product?.id && !uniqueProducts[product.id]) {
        uniqueProducts[product.id] = product
        result.push(product)
      }
    })

    return result
  }, [data])

  const isLoadingInitialData = !data && !error
  const isLoadingMore =
    isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined') || isValidating
  const isEmpty = data?.[0]?.data?.length === 0
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.data?.length < ROWS_PER_PAGE)

  return {
    products,
    isLoadingMore,
    isReachingEnd,
    isEmpty,
    setSize,
    error
  }
}
